import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import React from 'react';
import renderUtils from '../../helpers/renderUtils';
import CustomCard from '../CustomCard';

const ReservationTimeline = ({ cancellationRules, arrivalDate, departureDate, currentRefundStatus }) => {
  if (!cancellationRules) return null;

  const { partialRefundPercentage, refundDates } = cancellationRules;
  const { fullRefundDate, partialRefundStartDate, partialRefundEndDate, noRefundDate } = refundDates;

  const today = new Date();
  const startDate = arrivalDate ? new Date(arrivalDate) : null;
  const endDate = departureDate ? new Date(departureDate) : null;

  // Collect all dates for timeline
  const timelineEvents = [];

  // Collect all non-today dates first to determine if today should be shown
  const allDates = [];

  // Full refund period end
  if (fullRefundDate) {
    allDates.push(new Date(fullRefundDate));
    timelineEvents.push({
      date: new Date(fullRefundDate),
      title: "Pełny zwrot kończy się",
      subtitle: "100% zwrotu do tej daty",
      description: "Do tej daty włącznie możesz anulować rezerwację i otrzymać pełny zwrot wpłaconej kwoty.",
      type: 'fullRefund',
      color: 'success'
    });
  }

  // Partial refund period start
  if (partialRefundStartDate) {
    allDates.push(new Date(partialRefundStartDate));
    timelineEvents.push({
      date: new Date(partialRefundStartDate),
      title: "Częściowy zwrot rozpoczyna się",
      subtitle: `${partialRefundPercentage}% zwrotu od tej daty`,
      description: `Od tej daty anulowanie oznacza zwrot tylko ${partialRefundPercentage}% wpłaconej kwoty.`,
      type: 'partialStart',
      color: 'warning'
    });
  }

  // Partial refund period end
  if (partialRefundEndDate) {
    allDates.push(new Date(partialRefundEndDate));
    timelineEvents.push({
      date: new Date(partialRefundEndDate),
      title: "Częściowy zwrot kończy się",
      subtitle: `Ostatni dzień ${partialRefundPercentage}% zwrotu`,
      description: `Do tej daty włącznie jeszcze możesz otrzymać ${partialRefundPercentage}% zwrotu.`,
      type: 'partialEnd',
      color: 'warning'
    });
  }

  // No refund period start
  if (noRefundDate) {
    allDates.push(new Date(noRefundDate));
    timelineEvents.push({
      date: new Date(noRefundDate),
      title: "Brak zwrotu od tej daty",
      subtitle: "Brak zwrotu (0%)",
      description: "Od tej daty anulowanie rezerwacji nie uprawnia do żadnego zwrotu pieniędzy.",
      type: 'noRefund',
      color: 'error'
    });
  }

  // Reservation start date
  if (startDate) {
    allDates.push(startDate);
    timelineEvents.push({
      date: startDate,
      title: "Rozpoczęcie pobytu",
      subtitle: "Zameldowanie",
      description: "Planowana data rozpoczęcia pobytu w domku. Po tej dacie anulowanie nie jest już możliwe.",
      type: 'checkIn',
      color: 'primary'
    });
  }

  // Reservation end date
  if (endDate) {
    allDates.push(endDate);
    timelineEvents.push({
      date: endDate,
      title: "Zakończenie pobytu",
      subtitle: "Wymeldowanie",
      description: "Planowana data zakończenia pobytu i wymeldowania z domku.",
      type: 'checkOut',
      color: 'primary'
    });
  }

  // Only add "Today" if it falls within the timeline range
  const earliestDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : null;
  const latestDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : null;

  // Always show today
  timelineEvents.push({
    date: today,
    title: "Dzisiaj",
    subtitle: getCurrentStatusText(today, refundDates, partialRefundPercentage),
    description: "Aktualna data. Sprawdź swój status anulacji powyżej.",
    type: 'today',
    color: 'grey'
  });

  // Sort chronologically
  timelineEvents.sort((a, b) => a.date - b.date);

  // Determine current status
  const currentStatus = currentRefundStatus || getCurrentRefundStatus(today, refundDates);

  const cardTitle = (
    <div className="d-flex align-items-center gap-2">
      <span>Chronologia pobytu</span>
    </div>
  );

  return (
    <div className="reservation-timeline-container">
      <CustomCard
        width={12}
        className="mt-4"
        shadow={false}
        title={cardTitle}
      >


        {/* Current Status Info */}
        <div className="mb-4 p-3 rounded-3  border">
          <div className="text-center">
            <div className="mb-2">
              <strong className="fs-5">
                {getSimpleStatusText(currentStatus, partialRefundPercentage, refundDates)}
              </strong>
            </div>
          </div>
        </div>

        {/* Material UI Timeline */}
        <div className="timeline-container">
          <Timeline position="right">
            {timelineEvents.map((event, index) => (
              <TimelineItem key={index}>
                <TimelineOppositeContent color="textSecondary">
                  <div className="timeline-date-content">
                    <strong>{renderUtils.formatDate(event.date, true)}</strong>
                    <br />
                    <small className="text-muted">{event.subtitle}</small>
                  </div>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot
                    color={event.color === 'primary' ? 'primary' : event.color}
                    variant="outlined"
                    sx={{
                      width: 16,
                      height: 16,
                      ...(event.color === 'primary' && {
                        borderColor: '#4f46e5',
                        color: '#4f46e5'
                      }),
                      ...(event.type === 'today' && {
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(79, 70, 229, 0.7)' },
                          '70%': { boxShadow: '0 0 0 10px rgba(79, 70, 229, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(79, 70, 229, 0)' }
                        }
                      })
                    }}
                  />
                  {index < timelineEvents.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <div className="timeline-content-wrapper">
                    <h6 className="fw-bold mb-1">{event.title}</h6>
                    <p className="mb-0 text-muted small">{event.description}</p>
                  </div>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </div>
      </CustomCard>

      <style jsx>{`
        .timeline-container {
          background: #ffffff;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e9ecef;
        }

        .timeline-date-content {
          text-align: right;
          padding-right: 16px;
        }

        .timeline-content-wrapper {
          padding-left: 16px;
        }

        @media (max-width: 768px) {
          .timeline-date-content {
            text-align: left;
            padding-right: 8px;
          }
          
          .timeline-content-wrapper {
            padding-left: 8px;
          }
        }
      `}</style>
    </div>
  );
};

// Helper functions
function getCurrentRefundStatus(currentDate, refundDates) {
  const { fullRefundDate, partialRefundStartDate, noRefundDate } = refundDates;

  if (currentDate <= new Date(fullRefundDate)) {
    return 'FULL';
  } else if (currentDate <= new Date(partialRefundStartDate) && currentDate < new Date(noRefundDate)) {
    return 'PARTIAL';
  } else {
    return 'NONE';
  }
}

function getCurrentStatusText(currentDate, refundDates, partialRefundPercentage) {
  const status = getCurrentRefundStatus(currentDate, refundDates);
  return getStatusText(status, partialRefundPercentage);
}

function getStatusText(status, partialRefundPercentage) {
  switch (status) {
    case 'FULL': return 'Pełny zwrot (100%)';
    case 'PARTIAL': return `Częściowy zwrot (${partialRefundPercentage}%)`;
    case 'NONE': return 'Brak zwrotu (0%)';
    default: return 'Nieznany status';
  }
}



function getSimpleStatusText(status, partialRefundPercentage, refundDates) {
  const { fullRefundDate, partialRefundStartDate, partialRefundEndDate, noRefundDate } = refundDates;

  if (status === 'FULL') {
    return `Pełny zwrot (100%) do ${renderUtils.formatDate(fullRefundDate, true)}`;
  } else if (status === 'PARTIAL') {
    if (partialRefundEndDate) {
      return `Częściowy zwrot (${partialRefundPercentage}%) do ${renderUtils.formatDate(partialRefundEndDate, true)}`;
    } else {
      return `Częściowy zwrot (${partialRefundPercentage}%) od ${renderUtils.formatDate(partialRefundStartDate, true)}`;
    }
  } else if (status === 'NONE') {
    return `Brak zwrotu (0%) od ${renderUtils.formatDate(noRefundDate, true)}`;
  } else {
    return 'Nieznany status';
  }
}

export default ReservationTimeline; 